import type { EntrySource } from '~/graphql/generated/graphql';

import {
  UNIFIED_ROUTE_ABOUT,
  UNIFIED_ROUTE_ARTICLE_ROOT,
  UNIFIED_ROUTE_CAREERS,
  UNIFIED_ROUTE_COURSE,
  UNIFIED_ROUTE_INSTITUTION,
  UNIFIED_ROUTE_JOB_DETAIL,
  UNIFIED_ROUTE_LEARN,
  UNIFIED_ROUTE_VIDEOS,
} from '~/constants/routes/unified';
import { ENTRY_SOURCE } from '~/utils/unified/entryTracking';

interface EntrySourceRoute {
  path: string;
  /**
   * How the row claims a path — exactly one mode per row:
   * - `'exact'`: only `path` itself
   * - `'subtree'`: `path` and everything beneath it
   * - a number N: only paths exactly N segments beneath `path` (never `path` itself)
   */
  match: 'exact' | 'subtree' | number;
  entrySource: EntrySource;
}

/** Where a student who signed up from a path nothing here claims is recorded as having come from. */
const DEFAULT_ENTRY_SOURCE = ENTRY_SOURCE.ROOT_UNIFIED;

export interface EntrySourceRouteOptions {
  /**
   * Whether `/career-advice/:slug` is serving industry hubs (the hubs-enabled flag). Only that
   * path's lane depends on it, but every reader passes it: an omission would silently put a hub
   * visitor in the industry page's lane.
   */
  hubsEnabled: boolean;
}

/**
 * Which lane each public surface signs a student up into — the one statement of it.
 *
 * Two readers need this answer and must never disagree. A detail page knows the lane it belongs to
 * because it is that page; the global header does not, because it renders before any page setup has
 * run and the path is all it knows on the server. Both read the same rows, so a header link copied
 * from a job page and the page's own CTA land in the same lane.
 *
 * Every value is a real `EntrySource`: anything outside the enum is dropped when signup parses the
 * link, and a dropped source is an account that lands no lane at all.
 *
 * Ordered, first match wins — `/career-advice/:considerationArea/:articleSlug` (two segments deep)
 * is an article before it is a career, `/career-advice/:slug` (one segment) is an industry hub
 * once the hubs-enabled flag is on and an industry page until then, and `/career-advice` itself
 * is the library rather than one of the pages under it.
 */
export const entrySourceRoutes = ({ hubsEnabled }: EntrySourceRouteOptions): readonly EntrySourceRoute[] => [
  { path: UNIFIED_ROUTE_JOB_DETAIL, match: 'subtree', entrySource: ENTRY_SOURCE.JOB_PAGE },
  { path: UNIFIED_ROUTE_VIDEOS, match: 'subtree', entrySource: ENTRY_SOURCE.VIDEO_PAGE },
  // Detail pages only: the /institutions directory itself stays in the default lane.
  { path: UNIFIED_ROUTE_INSTITUTION, match: 1, entrySource: ENTRY_SOURCE.INSTITUTION_PAGE },
  { path: UNIFIED_ROUTE_COURSE, match: 1, entrySource: ENTRY_SOURCE.COURSE_PAGE },
  // Shape-based: mirrors pages/unified/career-advice/, where the article page is the only two-segment
  // route. Adding another two-segment page under /career-advice means revisiting this row.
  { path: UNIFIED_ROUTE_CAREERS, match: 2, entrySource: ENTRY_SOURCE.ARTICLE_PAGE },
  { path: UNIFIED_ROUTE_ARTICLE_ROOT, match: 1, entrySource: ENTRY_SOURCE.ARTICLE_PAGE },
  { path: UNIFIED_ROUTE_CAREERS, match: 1, entrySource: hubsEnabled ? ENTRY_SOURCE.HUB_PAGE : ENTRY_SOURCE.CAREER_PAGE },
  { path: UNIFIED_ROUTE_CAREERS, match: 'exact', entrySource: ENTRY_SOURCE.ROOT_UNIFIED_CAREER_ADVICE },
  { path: UNIFIED_ROUTE_CAREERS, match: 'subtree', entrySource: ENTRY_SOURCE.CAREER_PAGE },
  { path: UNIFIED_ROUTE_LEARN, match: 'subtree', entrySource: ENTRY_SOURCE.ROOT_UNIFIED_LEARN },
  { path: UNIFIED_ROUTE_ABOUT, match: 'exact', entrySource: ENTRY_SOURCE.ABOUT },
];

const segmentsBeneath = (base: string, path: string): number | null =>
  (path.startsWith(`${base}/`) ? path.slice(base.length + 1).split('/').filter(Boolean).length : null);

const claims = (route: EntrySourceRoute, path: string): boolean => {
  if (typeof route.match === 'number') {
    return segmentsBeneath(route.path, path) === route.match;
  }
  if (route.match === 'subtree') {
    return path === route.path || path.startsWith(`${route.path}/`);
  }
  return path === route.path;
};

export const entrySourceForPath = (path: string, options: EntrySourceRouteOptions): EntrySource =>
  entrySourceRoutes(options).find(route => claims(route, path))?.entrySource ?? DEFAULT_ENTRY_SOURCE;
